import { Injectable, OnDestroy } from '@angular/core';
import { ApiService } from '../../core/api/api.service';
import { ResourceUpload } from '../../core/firebase/resource-upload';
import { ModuleStateService } from '../../module-state.service';
import { SmService } from 'supermappe-core';
import { InputDialogService } from '../../shared/dialog/input-dialog.service';
import { Observable, Observer, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { extract } from '../../core/i18n.service';
import { Logger } from '../../core/logger.service';
import { ConfirmationService } from '../../shared/dialog/confirmation.service';
import { Ng2ImgMaxService } from 'ng2-img-max';
import { ImageUtilsService } from '../../core/image-utils-service.service';
import { STATS } from '../../../../../commondata/stats/stats-constants';
import { StatDto } from '../../core/stats/stat-dto';
import { MatomoTracker } from 'ngx-matomo-client';

const logger = new Logger('ImageMapsService');

@Injectable({
    providedIn: 'root'
})
export class ImageMapsService implements OnDestroy {

    insertImageSubscription: Subscription | undefined;
    uploadImageFromUriSubscription: Subscription | undefined;
    confirmSubscription: Subscription | undefined;
    askUrlSubscription: Subscription | undefined;
    openDialogSubscription: Subscription | undefined;
    insertImageFromDriveSubscription: Subscription | undefined;
    insertImageFromSvgSubscription: Subscription | undefined;

    private resizeImageLimit: number = 1500;

    constructor(
        private smService: SmService,
        private moduleStateService: ModuleStateService,
        private translateService: TranslateService,
        private apiService: ApiService,
        private inputDialogService: InputDialogService,
        private ng2ImgMax: Ng2ImgMaxService,
        private confirmationService: ConfirmationService,
        private imageUtils: ImageUtilsService,
        private tracker: MatomoTracker) {
    }

    ngOnDestroy() {
        if (this.insertImageSubscription) { this.insertImageSubscription.unsubscribe(); }
        if (this.uploadImageFromUriSubscription) { this.uploadImageFromUriSubscription.unsubscribe(); }
        if (this.confirmSubscription) { this.confirmSubscription.unsubscribe(); }
        if (this.askUrlSubscription) { this.askUrlSubscription.unsubscribe(); }
        if (this.openDialogSubscription) { this.openDialogSubscription.unsubscribe(); }
        if (this.insertImageFromDriveSubscription) { this.insertImageFromDriveSubscription.unsubscribe(); }
    }

    private uploadImageFromFile(compressedImage: any, node: any) {
        const mapId = this.moduleStateService.docId;
        // const data = this.getNode();
        let data: any;
        if (node) {
            data = node;
        } else {
            data = this.getNode();
        }
        if (data.node || node) {
            let targetNodeId: string;
            if (data.node) {
                targetNodeId = data.node.id;
            } else {
                targetNodeId = data.id;
            }
            this.smService.setImagePlaceholderToNodeId(targetNodeId, true);
            const currentImageUpload = new ResourceUpload(compressedImage);
            if (this.insertImageSubscription) { this.insertImageSubscription.unsubscribe(); }
            this.insertImageSubscription = this.apiService.insertImage(currentImageUpload, mapId, targetNodeId)
                .subscribe({
                    next: (image: any) => {
                        this.insertImageFromUri(image.url, image.destPath, targetNodeId, data.created);
                    },
                    error: (error: any) => {
                        let errorCode = '';
                        if (error.status === 504) {
                            errorCode = this.translateService.instant(extract('OFFLINE_GENERIC'));
                        } else {
                            errorCode = (error.code) ? error.code : 'ERROR_INSERT_IMAGE';
                        }
                        this.smService.setImagePlaceholderToNodeId(targetNodeId, false);
                        this.moduleStateService.setStateError(errorCode);
                        if (data.created) {
                            this.smService.deleteSelection();
                        }
                    }
                });
        } else {
            this.moduleStateService.setStateError('ERROR_INSERT_IMAGE');
            if (data.created) {
                this.smService.deleteSelection();
            }
        }

    }

    resizeImage(file: File): Observable<any> {
        return this.ng2ImgMax.resizeImage(file, this.resizeImageLimit, this.resizeImageLimit);
    }

    insertImageFromFile(currentImageFile: File, node: any) {
        if (currentImageFile) {

            /*this.imageUtils.resizeImage(currentImageFile, this.resizeImageLimit).then((resizedImage) => {
                this.uploadImageFromFile(resizedImage, node);
            }).catch((error: any) => {
                let errorCode = '';
                if (error.status === 504) {
                    errorCode = this.translateService.instant(extract('OFFLINE_GENERIC'));
                } else {
                    errorCode = error.error.message;
                }
                console.log(errorCode);

                this.uploadImageFromFile(currentImageFile, node);
            });*/


            this.resizeImage(currentImageFile).subscribe({
                next: (resizedImage: any) => {
                    this.uploadImageFromFile(resizedImage, node);
                },
                error: (error: any) => {
                    let errorCode = '';
                    if (error.status === 504) {
                        errorCode = this.translateService.instant(extract('OFFLINE_GENERIC'));
                    } else {
                        errorCode = error.error.message;
                    }
                    console.log(error);
                    this.uploadImageFromFile(currentImageFile, node);
                }
            });
        }
    }

    insertImageFromSvg(svg: string, targetNodeId: any) {
        if (svg) {
            const mapId = this.moduleStateService.docId;
            this.smService.setImagePlaceholderToNodeId(targetNodeId, true);
            // const currentImageUpload = new ResourceUpload(currentImageFile);
            if (this.insertImageSubscription) { this.insertImageSubscription.unsubscribe(); }
            this.insertImageFromSvgSubscription = this.apiService.insertImageFromSvg(svg, mapId, targetNodeId)
                .subscribe({
                    next: (image: any) => {
                        this.insertImageFromUri(image.url, image.destPath, targetNodeId, true);
                    },
                    error: (error: any) => {
                        let errorCode = '';
                        if (error.status === 504) {
                            errorCode = this.translateService.instant(extract('OFFLINE_GENERIC'));
                        } else {
                            errorCode = (error.code) ? error.code : 'ERROR_INSERT_IMAGE';
                        }
                        this.smService.setImagePlaceholderToNodeId(targetNodeId, false);
                        this.moduleStateService.setStateError(errorCode);
                    }
                });
        }
    }

    openDialog(urlToOpen: string) {
        return new Observable((observer: Observer<boolean>) => {
            this.moduleStateService.setFocusOnMap(false);
            this.moduleStateService.lockUI(false, false);
            if (this.askUrlSubscription) { this.askUrlSubscription.unsubscribe(); }
            this.askUrlSubscription = this.inputDialogService.askUrl(this.translateService.instant(extract('INSERT-IMAGE')),
                this.translateService.instant(extract('INPUT-IMAGE-URL')), '',
                this.moduleStateService.docId, urlToOpen)
                .subscribe({
                    next: (result) => {
                        this.moduleStateService.setFocusOnMap(true);
                        if (result === '') { result = false; }
                        observer.next(result);
                        observer.complete();
                    },
                    error: (error: any) => {
                        this.moduleStateService.setFocusOnMap(true);
                        observer.next(false);
                        observer.complete();
                    }
                });
        });
    }

    // private lockUi(locked: boolean) {
    //   this.moduleStateService.setEditingState(locked);
    //   this.moduleStateService.setBusyState(locked);
    //   this.moduleStateService.setCanEnableQuickEdit(!locked);
    //   this.smService.setEnableKeyPresses(!locked);
    // }

    insertImageFromDrive(googleId: string, storagePath?: string, targetNodeIdOutside?: string, nodeCreatedOutside?: boolean) {

        if (navigator.onLine) {
            let targetNodeId = '';
            let data: any;

            if (targetNodeIdOutside) {
                nodeCreatedOutside = true;
                targetNodeId = targetNodeIdOutside;
            } else {
                data = this.getNode();
                targetNodeId = data.node.id;
            }

            if (targetNodeId) {
                this.smService.setImagePlaceholderToNodeId(targetNodeId, true);
                const mapId = this.moduleStateService.docId;
                this.moduleStateService.lockUI(true, true);
                if (this.insertImageFromDriveSubscription) { this.insertImageFromDriveSubscription.unsubscribe(); }

                this.insertImageFromDriveSubscription = this.apiService.insertImageFromDrive(googleId, mapId, targetNodeId, storagePath)
                    .subscribe({
                        next: (imageData: any) => {
                            const imageInfo = { name: imageData.res, transparent: false, custom: false };
                            this.smService.setImageToNodeId(targetNodeId, imageInfo, false);
                            this.moduleStateService.lockUI(false, false);
                            this.moduleStateService.dropping = false;
                            this.moduleStateService.mapOperations.push(new StatDto(STATS.MAP.IMAGE_INSERT));
                        },
                        error: (error: any) => {
                            if (error.status === 504) {
                                error = this.translateService.instant(extract('OFFLINE_GENERIC'));
                            } else {
                                error = 'ERROR_INSERT_IMAGE';
                            }
                            this.moduleStateService.dropping = false;
                            this.smService.setImagePlaceholderToNodeId(targetNodeId, false);
                            this.moduleStateService.lockUI(false, false);
                            if (this.confirmSubscription) { this.confirmSubscription.unsubscribe(); }
                            this.confirmSubscription = this.confirmationService.confirm(
                                this.translateService.instant(extract('WARNING')),
                                this.translateService.instant(extract(error)),
                                this.translateService.instant(extract('BUTTON_OK')),
                                '').subscribe(result => {
                                    if (data.created || nodeCreatedOutside) {
                                        this.smService.deleteSelection();
                                    }
                                });
                        }
                    });
            } else {
                this.moduleStateService.dropping = false;
            }
        } else {
            const error = this.translateService.instant(extract('OFFLINE_GENERIC'));
            this.moduleStateService.dropping = false;
            this.moduleStateService.lockUI(false, false);
            if (this.confirmSubscription) { this.confirmSubscription.unsubscribe(); }
            this.confirmationService.confirm(
                this.translateService.instant(extract('WARNING')),
                this.translateService.instant(extract(error)),
                this.translateService.instant(extract('BUTTON_OK')),
                '');
        }
    }

    insertImageFromUriWithDialog() {
        let uri: string;
        this.moduleStateService.lockUI(true, true);
        if (this.openDialogSubscription) { this.openDialogSubscription.unsubscribe(); }
        this.openDialogSubscription = this.openDialog('')
            .subscribe((result: any) => {
                if (result) {
                    uri = result.name;
                    this.insertImageFromUri(uri);
                } else {
                    this.moduleStateService.lockUI(false, false);
                }
            });
    }

    insertImageFromUri(
        uri: string,
        storagePath?: string,
        targetNodeIdOutside?: string,
        nodeCreatedOutside?: boolean,
        callback?: (success: boolean, data: any) => void
    ) {
        if (navigator.onLine) {
            let targetNodeId = '';
            let data: any;
            if (targetNodeIdOutside) {
                nodeCreatedOutside = true;
                targetNodeId = targetNodeIdOutside;
            } else {
                data = this.getNode();
                targetNodeId = data.node.id;
            }
            if (targetNodeId) {
                this.smService.setImagePlaceholderToNodeId(targetNodeId, true);
                const mapId = this.moduleStateService.docId;
                if (this.uploadImageFromUriSubscription) { this.uploadImageFromUriSubscription.unsubscribe(); }
                this.uploadImageFromUriSubscription = this.apiService.uploadImageFromUri(uri, mapId, targetNodeId, storagePath)
                    .subscribe({
                        next: (imageData: any) => {
                            const imageInfo = { name: imageData.res, transparent: false, custom: false };
                            this.smService.setImageToNodeId(targetNodeId, imageInfo, false);
                            this.moduleStateService.lockUI(false, false);
                            this.moduleStateService.dropping = false;
                            if (this.moduleStateService.mapOperations) {
                                this.moduleStateService.mapOperations.push(new StatDto(STATS.MAP.IMAGE_INSERT));
                            }

                            //MATOMO Map insert image
                            try {
                                console.log('MATOMO event Map insert image');
                                this.tracker.trackEvent('Map', 'Insert image', 'InsertImage');
                            } catch (e: any) {
                                logger.error('matomo error ' + e.message);
                            }

                            if (callback) callback(true, imageInfo);
                        },
                        error: (error: any) => {
                            let errorMessage = '';
                            if (error.status === 504) {
                                errorMessage = this.translateService.instant(extract('OFFLINE_GENERIC'));
                            } else {
                                errorMessage = 'ERROR_INSERT_IMAGE';
                            }
                            this.moduleStateService.dropping = false;
                            this.smService.setImagePlaceholderToNodeId(targetNodeId, false);
                            this.moduleStateService.lockUI(false, false);
                            if (this.confirmSubscription) { this.confirmSubscription.unsubscribe(); }
                            if (!uri.startsWith('https://lh3.googleusercontent.com/d/')) {
                                this.confirmSubscription = this.confirmationService.confirm(
                                    this.translateService.instant(extract('WARNING')),
                                    this.translateService.instant(extract(errorMessage)),
                                    this.translateService.instant(extract('BUTTON_OK')),
                                    ''
                                ).subscribe(result => {
                                    if (data.created || nodeCreatedOutside) {
                                        this.smService.deleteSelection();
                                    }
                                    if (callback) callback(false, errorMessage);
                                });
                            } else {
                                if (callback) callback(false, errorMessage);
                            }
                        }
                    });
            } else {
                this.moduleStateService.dropping = false;
                if (callback) callback(false, 'No target node ID found.');
            }
        } else {
            const errorMessage = this.translateService.instant(extract('OFFLINE_GENERIC'));
            this.moduleStateService.dropping = false;
            this.moduleStateService.lockUI(false, false);
            if (this.confirmSubscription) { this.confirmSubscription.unsubscribe(); }
            this.confirmationService.confirm(
                this.translateService.instant(extract('WARNING')),
                this.translateService.instant(extract(errorMessage)),
                this.translateService.instant(extract('BUTTON_OK')),
                ''
            );
            if (callback) callback(false, errorMessage);
        }
    }

    insertImageFromUriPromise(
        uri: string,
        storagePath?: string,
        targetNodeIdOutside?: string,
        nodeCreatedOutside?: boolean
    ) {
        return new Promise((resolve, reject) => {
            this.insertImageFromUri(uri, storagePath, targetNodeIdOutside, nodeCreatedOutside, (success: boolean, data: any) => {
                if (success) {
                    return resolve(data);
                } else {
                    return resolve(null);
                }
            });
        });
    }

    private getNode(): any {
        const result = { node: {}, created: false };
        result.node = this.smService.getSelectedNode();
        if (result.node === null) {
            result.node = this.smService.addNodeAndSelect(this.moduleStateService.selectionData.scrCenterX, this.moduleStateService.selectionData.scrCenterY);
            this.moduleStateService.mapOperations.push(new StatDto(STATS.MAP.NODE_CREATE));
            result.created = true;
        }
        return result;
    }

}
